import { useEffect, useRef, useState } from "react";
import zoomSdk from "@zoom/appssdk";
import {
  AUTO_START_MODES,
  RTMS_REQUESTER_DEFAULT_NAME,
  RTMS_START_TIMING_MS,
  START_DISCLAIMER_CHAT_MESSAGE,
  TRANSCRIPTION_STATES,
} from "../../constants";
import LogManager from "../../utils/logManager";
import { fetchRtmsSessionStatus, isRtmsError, SESSION_STATUS, SESSION_STATUS_MESSAGE } from "../../utils/rtmsSessionStatusUtils";
import { LEO_INTERVIEW_CHAT_MESSAGE } from "./hire.constants";
import { updateRtms } from "./hire.utils";

/**
 * RTMS transcription lifecycle for a HIRE meeting: start / end, the Zoom SDK status listener,
 * session-status polling after start, rejection when someone else is starting, RTMS error +
 * restart, and auto-start. State is seeded from the meeting initialization (useInMeetingInitialization)
 * and owned here after mount.
 */
export const useHireTranscription = ({
  role,
  meetingId,
  meetingUuid,
  alias,
  appContext,
  autoStartMode,
  initialTranscriptionState,
  initialIsRtmsStartedByOther,
  initialRtmsRequesterName,
  initialIsAutostarting,
  leoInterviewer,
}) => {
  const [transcriptionState, setTranscriptionState] = useState(
    initialIsAutostarting ? TRANSCRIPTION_STATES.LOADING : initialTranscriptionState
  );
  const [isRtmsStartedByOther, setIsRtmsStartedByOther] = useState(initialIsRtmsStartedByOther);
  const [rtmsRequesterName, setRtmsRequesterName] = useState(initialRtmsRequesterName);
  const [isServerErrorOpen, setIsServerErrorOpen] = useState(false);
  const [rtmsErrorType, setRtmsErrorType] = useState(null);

  const isPerHostTranscription = autoStartMode === AUTO_START_MODES.MULTIPLE;
  const rtmsArgs = [meetingUuid, meetingId, role, appContext];

  const pollIntervalRef = useRef(null);
  const startTimeoutRef = useRef(null);
  const rejectionTimeoutRef = useRef(null);
  // Whether this user initiated the current start (per-host mode only reacts to its own start)
  const userInitiatedStartRef = useRef(false);
  // rtmsSessionId from the SDK "started" event, to match "stopped" events in per-host mode
  const rtmsSessionIdRef = useRef(null);
  // The SDK listener and async start / timers read these instead of stale state
  const isRtmsStartedByOtherRef = useRef(initialIsRtmsStartedByOther);
  isRtmsStartedByOtherRef.current = isRtmsStartedByOther;
  const transcriptionStateRef = useRef(transcriptionState);
  transcriptionStateRef.current = transcriptionState;

  // Stop watching a start attempt and go back to the Start screen.
  const resetStart = () => {
    clearInterval(pollIntervalRef.current);
    clearTimeout(startTimeoutRef.current);
    userInitiatedStartRef.current = false;
    setTranscriptionState(TRANSCRIPTION_STATES.DEFAULT);
  };

  // Post the start disclaimer, plus the export-control reminder for LEO interviewers.
  const sendStartMessagesToZoomChat = async () => {
    try {
      await zoomSdk.sendMessageToChat({ message: START_DISCLAIMER_CHAT_MESSAGE });
      if (leoInterviewer) {
        await zoomSdk.sendMessageToChat({ message: LEO_INTERVIEW_CHAT_MESSAGE });
        LogManager.info("[Hire] Sent LEO interview reminder message");
      }
    } catch (error) {
      LogManager.error("[Hire] Error sending start message:", error);
    }
  };

  const handleStarted = async (event) => {
    if (isPerHostTranscription) {
      if (!userInitiatedStartRef.current) return;
      userInitiatedStartRef.current = false;
      setIsRtmsStartedByOther(false);
      sendStartMessagesToZoomChat();
      rtmsSessionIdRef.current = event.rtmsSessionId || null;
    } else {
      try {
        const sessionStatus = await fetchRtmsSessionStatus(meetingUuid);
        const isMine = sessionStatus.rtmsRequesterAlias && sessionStatus.rtmsRequesterAlias === alias;
        LogManager.info(`[Hire] RTMS started by ${isMine ? "this user" : sessionStatus.rtmsRequesterAlias}`);
        setIsRtmsStartedByOther(!isMine);
        if (isMine) {
          sendStartMessagesToZoomChat();
        } else {
          setRtmsRequesterName(sessionStatus.rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME);
        }
      } catch (error) {
        LogManager.error("[Hire] Error fetching session status on RTMS start:", error);
        setIsRtmsStartedByOther(true);
        setRtmsRequesterName(RTMS_REQUESTER_DEFAULT_NAME);
      }
    }
    // Session is up: stop the start poll too, or a late poll would undo an End in progress.
    clearInterval(pollIntervalRef.current);
    clearTimeout(startTimeoutRef.current);
    clearTimeout(rejectionTimeoutRef.current);
    setTranscriptionState(TRANSCRIPTION_STATES.TRANSCRIBING);
  };

  const handleStopped = async (event) => {
    if (isPerHostTranscription) {
      // Only this user's own session; ignore all stops if we never started one.
      if (!rtmsSessionIdRef.current) return;
      if (event.rtmsSessionId && event.rtmsSessionId !== rtmsSessionIdRef.current) return;
      rtmsSessionIdRef.current = null;
    }
    const wasStartedByOther = isRtmsStartedByOtherRef.current;
    setIsRtmsStartedByOther(false);
    setRtmsRequesterName(null);
    setTranscriptionState(TRANSCRIPTION_STATES.STOPPED);

    try {
      const sessionStatus = await fetchRtmsSessionStatus(meetingUuid);
      // Only the user who started RTMS sees the error modal
      if (isRtmsError(sessionStatus.statusMessage) && !wasStartedByOther) {
        resetStart();
        setRtmsErrorType(sessionStatus.statusMessage);
      }
    } catch (error) {
      LogManager.error("[Hire] Error fetching session status on RTMS stop:", error);
    }
  };

  // Poll session status after a successful start request until ACTIVE, an RTMS error, or timeout.
  const watchStart = (isRestartAttempt) => {
    pollIntervalRef.current = setInterval(async () => {
      try {
        const sessionStatus = await fetchRtmsSessionStatus(meetingUuid);
        if (sessionStatus.status === SESSION_STATUS.ACTIVE) {
          LogManager.info("[Hire] Session is ACTIVE");
          clearInterval(pollIntervalRef.current);
          clearTimeout(startTimeoutRef.current);
          setTranscriptionState(TRANSCRIPTION_STATES.TRANSCRIBING);
        } else if (isRtmsError(sessionStatus.statusMessage)) {
          LogManager.info(`[Hire] RTMS error while starting: ${sessionStatus.statusMessage}`);
          resetStart();
          setRtmsErrorType(sessionStatus.statusMessage);
        }
      } catch (error) {
        LogManager.error("[Hire] Error polling session status:", error);
      }
    }, RTMS_START_TIMING_MS.STATUS_POLL);

    startTimeoutRef.current = setTimeout(async () => {
      try {
        const sessionStatus = await fetchRtmsSessionStatus(meetingUuid);
        if (sessionStatus.status !== SESSION_STATUS.ACTIVE) {
          LogManager.info(`[Hire] Session still ${sessionStatus.status} after start timeout`);
          resetStart();
          setRtmsErrorType(SESSION_STATUS_MESSAGE.RTMS_INIT_ERROR);
        }
      } catch (error) {
        LogManager.error("[Hire] Error checking final session status:", error);
        resetStart();
        setIsServerErrorOpen(true);
      }
    }, isRestartAttempt ? RTMS_START_TIMING_MS.RESTART_START_TIMEOUT : RTMS_START_TIMING_MS.START_TIMEOUT);
  };

  const requestStart = async (isRestartAttempt = false) => {
    setTranscriptionState(TRANSCRIPTION_STATES.LOADING);
    if (isRestartAttempt) {
      await new Promise((resolve) => setTimeout(resolve, RTMS_START_TIMING_MS.RESTART_DELAY));
    }
    userInitiatedStartRef.current = true;
    LogManager.info(`[Hire] Starting RTMS${isRestartAttempt ? " (restart)" : ""}`);
    const response = await updateRtms("start", ...rtmsArgs);
    LogManager.info(`[Hire] RTMS start result: ${response.result}`);

    if (response.result === "OK") {
      watchStart(isRestartAttempt);
    } else if (response.result === "REJECTED") {
      // Someone else is already starting; reset if no "started" event arrives.
      userInitiatedStartRef.current = false;
      // Their "started" event can arrive before this response; keep the active session.
      if (transcriptionStateRef.current !== TRANSCRIPTION_STATES.TRANSCRIBING) {
        setTranscriptionState(TRANSCRIPTION_STATES.REJECTED);
        setRtmsRequesterName(response.rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME);
        rejectionTimeoutRef.current = setTimeout(() => {
          if (transcriptionStateRef.current === TRANSCRIPTION_STATES.REJECTED) {
            setTranscriptionState(TRANSCRIPTION_STATES.DEFAULT);
            setRtmsRequesterName(null);
          }
        }, RTMS_START_TIMING_MS.REJECTED_RESET);
      }
    } else {
      resetStart();
      setIsServerErrorOpen(true);
    }
  };

  const start = () => {
    if (transcriptionState !== TRANSCRIPTION_STATES.LOADING) requestStart();
  };

  const end = async () => {
    if (transcriptionState === TRANSCRIPTION_STATES.LOADING) return;
    setTranscriptionState(TRANSCRIPTION_STATES.LOADING);
    LogManager.info("[Hire] Stopping RTMS");
    const response = await updateRtms("stop", ...rtmsArgs);
    LogManager.info(`[Hire] RTMS stop result: ${response.result}`);
    // On success the SDK "stopped" event moves us to STOPPED.
    if (response.result !== "OK") {
      setTranscriptionState(TRANSCRIPTION_STATES.TRANSCRIBING);
      setIsServerErrorOpen(true);
    }
  };

  // SDK listener; registered before the auto-start below.
  useEffect(() => {
    try {
      zoomSdk.onRTMSStatusChange((event) => {
        LogManager.info("[Hire] RTMS status changed:", JSON.stringify(event));
        if (event.status === "started") handleStarted(event);
        else if (event.status === "stopped") handleStopped(event);
      });
    } catch (error) {
      LogManager.error("[Hire] Error setting up RTMS status listener:", error);
    }
    if (initialIsAutostarting) {
      LogManager.info("[Hire] Auto-starting transcription");
      requestStart();
    }
    return () => {
      clearInterval(pollIntervalRef.current);
      clearTimeout(startTimeoutRef.current);
      clearTimeout(rejectionTimeoutRef.current);
    };
  }, []);

  return {
    transcriptionState,
    isRtmsStartedByOther,
    rtmsRequesterName,
    start,
    end,
    isServerErrorOpen,
    closeServerError: () => setIsServerErrorOpen(false),
    rtmsErrorType,
    cancelRtmsError: () => setRtmsErrorType(null),
    restartAfterRtmsError: () => {
      setRtmsErrorType(null);
      requestStart(true);
    },
  };
};
